import { Box, Button, Group, Table } from "@mantine/core";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import classes from "./flagsAdvancedSearch.module.scss";
import { AppPagination } from "../../../../components/AppPagination/AppPagination";
import PageTitle from "../../../../components/Page/PageTitle";
import PageWrapper from "../../../../components/Page/PageWrapper";
import { TableSkeleton } from "../../../../components/Skeletons/TableSkeleton";
import { TableNoResults } from "../../../../components/TableNoResults/TableNoResults";
import ThrottledTextInput from "../../../../components/ThrottledTextInput";
import SortIcon from "../../../../components/searchOptions/SortIcon";
import DateSearchFilter from "../../../../components/searchOptions/dateSearchFilter/DateSearchFilter";
import useFlagsAdvancedSearch from "./useFlagsAdvancedSearch.logic";
import { dateFormat } from "../../../../utils/dateFormat";

const COLUMNS = [
  {
    key: "id",
    labelKey: "monitoringQueue.flag",
    width: 90,
    thWidth: 190,
  },
  {
    key: "date",
    labelKey: "monitoringQueue.date",
    sortKey: "DATE",
    isDate: true,
    sortable: true,
    thWidth: 290,
  },
  {
    key: "name",
    labelKey: "advancedSearchHistory.reportName",
    filterKey: "ruleName",
    width: 160,
  },
];

export default function FlagsAdvancedSearch() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    filters,
    handleFilterChange,
    handlePageChange,
    handleTakeChange,
    id,
    isFetching,
    page,
    rowsData,
    sort,
    take,
    toggleSort,
    totalCount,
  } = useFlagsAdvancedSearch();

  const handleRowClick = (flagId) => {
    navigate(`/reports-custom/flag-details-advanced-search/${flagId}`);
  };

  const handleRedirectToTemplate = () => {
    navigate(`/advanced-search-history/rule-dashboard/${id}`);
  };

  const rows = rowsData.map((row, index) => (
    <Table.Tr onClick={() => handleRowClick(row.id)} key={`${row.id}-${index}`}>
      <Table.Td>{row.id}</Table.Td>
      <Table.Td>{dayjs(row.date).format(dateFormat)}</Table.Td>
      <Table.Td>{row.name}</Table.Td>
    </Table.Tr>
  ));

  return (
    <PageWrapper>
      <PageTitle title={t("pageHeaders.flagsAdvancedSearch")} />

      <Group mb="md">
        <Button
          onClick={handleRedirectToTemplate}
          variant="light"
          radius="md"
          ml={"16px"}
        >
          View Rule Report
        </Button>
      </Group>

      <Box className="table-panel">
        <Table
          horizontalSpacing="md"
          highlightOnHover
          stickyHeader
          className={classes["flags-advanced-search-table"]}
          style={{ tableLayout: "fixed" }}
        >
          <Table.Thead>
            <Table.Tr>
              {COLUMNS.map((column) => (
                <Table.Th
                  key={column.key}
                  w={column.thWidth}
                  miw={column.thWidth ? undefined : 200}
                >
                  <Group>
                    {column.sortable ? (
                      <Group
                        style={{ cursor: "pointer" }}
                        onClick={() => toggleSort(column.sortKey)}
                        gap={4}
                        wrap="nowrap"
                      >
                        {t(column.labelKey)}
                        <SortIcon col={column.sortKey} sort={sort} />
                      </Group>
                    ) : (
                      <Group gap={4} wrap="nowrap">
                        {t(column.labelKey)}
                      </Group>
                    )}

                    {column.isDate ? (
                      <DateSearchFilter
                        placeholder={t(column.labelKey)}
                        onChange={(value) => handleFilterChange("DATE", value)}
                      />
                    ) : (
                      <ThrottledTextInput
                        size="xs"
                        w={column.width || "auto"}
                        delay={600}
                        value={filters[column.filterKey || column.key]}
                        onThrottledChange={(value) =>
                          handleFilterChange(
                            column.filterKey || column.key,
                            value,
                          )
                        }
                      />
                    )}
                  </Group>
                </Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>

          {rowsData.length === 0 && !isFetching ? (
            <Table.Tbody>
              <TableNoResults colSpan={COLUMNS.length} />
            </Table.Tbody>
          ) : (
            <RenderTableWithSkeleton />
          )}
        </Table>
      </Box>
      {totalCount > 0 && (
        <AppPagination
          page={page}
          take={take}
          total={totalCount}
          onPageChange={handlePageChange}
          onTakeChange={handleTakeChange}
        />
      )}
    </PageWrapper>
  );

  function RenderTableWithSkeleton() {
    if (isFetching) {
      return <TableSkeleton cols={COLUMNS.length} length={take} />;
    }

    return <Table.Tbody pos="relative">{rows}</Table.Tbody>;
  }
}
